import React, { useEffect, useState, useRef } from "react";
import { Button, Popconfirm, Dropdown, Space, Form, Modal, Popover } from "antd";
import { ReactSVG } from "react-svg";
import { Link } from "react-router-dom";
import socketIOClient from "socket.io-client";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

import ListContainer from "../../modules/order/containers/List";
import OrdersTable from "../Table/Orders";
import OrdersFilterForm from "../Forms/Filters/Orders";
import BulkDeleteForm from "../Forms/Orders/BulkDelete";
import BulkUpdateForm from "../Forms/Orders/BulkUpdate";
import withPage from "./withPage";
import { PAGE_SIZE } from "../../constants";
import "./Orders.scss";
import RoutePlanCurrent from "../RoutePlan/RoutePlanCurrent";

import RoutePlanForm from "../Forms/RoutePlans/RoutePlan";
import BulkAddToRoutePlanForm from "../Forms/Orders/BulkAddToRoutePlan";
import BulkUpdateDeliveryDateForm from "../Forms/Orders/BulkUpdateDeliveryDate";
import OrderTypeForm from "../Forms/Orders/OrderType";

import { doCreateRoutePlan } from "../../modules/routePlan/stores/thunk";
import { doAddOrdersRoutePlan } from "../../modules/routePlan/stores/thunk";
import { selectAllRoutePlans } from "../../modules/routePlan/stores/slice";
import { DATE_FORMAT_QUERY } from "../../constants";
import { getTranslation } from "../../utils/helper";

import { doGetAllLabels } from "../../modules/label/stores/thunk";
import { selectAllLabels } from "../../modules/label/stores/slice";

import { doUpdateSettingNoFetching } from "../../modules/setting/stores/thunk";
import Cookies from "js-cookie";

const socketURI = process.env.REACT_APP_SOCKET_URI || "";
const socket = socketIOClient(socketURI);

const A_DELETE = "A_DELETE";
const A_UPDATE = "A_UPDATE";
const A_BULK_UPDATE_DELIVERY_DATE = "A_BULK_UPDATE_DELIVERY_DATE";
const A_ADD_ORDER_TO_NEW_ROUTE = "A_ADD_ORDER_TO_NEW_ROUTE";
const A_ADD_ORDER_TO_EXIST_ROUTE = "A_ADD_ORDER_TO_EXIST_ROUTE";

const defaultCountryCode = window.location.href.includes("nordicrouting.se") ? "se" : "sg";

const OrdersPage = ({
  isFetching,
  orders,
  metadata,
  query,
  history,
  pushTo,
  message,
  doGetAllOrders,
  doDeleteOrder,
  doBulkDeleteOrders,
  doBulkUpdateOrders,
  doBulkUpdateDeliveryDate,
  user,
}) => {
  const setting = useSelector(state => state.setting.setting) || user.company;
  const roles = user.roles;
  const [showFilters, setShowFilters] = useState(false);
  const [defaultSelectedItems, setDefaultSelectedItems] = useState([]);
  const [isSelectAll, setIsSelectAll] = useState(false);

  const currentPage = Number(query.currentPage) || 1;
  const itemsPerPage = Number(query.itemsPerPage) || PAGE_SIZE;
  const pagination = {
    current: currentPage,
    pageSize: itemsPerPage,
    total: metadata.totalItems,
    showTotal: total => `${total} ${getTranslation(setting, "Orders", "Orders")}`,
  };

  const [modifying, setModifying] = useState(false);
  const [form] = Form.useForm();
  const [state, setState] = useState({
    visibleModal: false,
    type: "",
    items: [],
  });
  const [typeNewOrder] = useState();

  const [countryCodeInit, setCountryCodeInit] = useState(defaultCountryCode);

  const mountedRef = useRef(true);

  useEffect(() => {
    setCountryCodeInit(Cookies.get(`CountryCode-${user._id}`) || defaultCountryCode);
  }, []);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const allLabels = selectAllLabels(useSelector(state => state));

  useEffect(() => {
    dispatch(doGetAllLabels({}));
  }, []);

  const routePlans = selectAllRoutePlans(useSelector(state => state));
  const stateRoutePlan = useSelector(state => state.routePlan);
  const messageRoutePlan = stateRoutePlan.message;
  const isFetchingRoutePlan = stateRoutePlan.isFetching;
  const dispatch = useDispatch();

  const canEdit = !user?.roles?.includes("user");

  const ACTIONS = [
    [A_DELETE, getTranslation(setting, "Orders", "Delete"), "delete"],
    [A_UPDATE, getTranslation(setting, "Orders", "Bulk Update"), "update"],
    [A_BULK_UPDATE_DELIVERY_DATE, getTranslation(setting, "Orders", "Bulk Update Date"), "update-date"],
    [A_ADD_ORDER_TO_NEW_ROUTE, getTranslation(setting, "Orders", "Create New Route Plan with Selected Orders"), "add"],
    [A_ADD_ORDER_TO_EXIST_ROUTE, getTranslation(setting, "Orders", "Add to Existing Route Plan"), "add-circle"],
  ];

  const BULK_FAILED = {
    A_ADD_ORDER_TO_NEW_ROUTE_FAILED: {
      title: getTranslation(setting, "Orders", "Failed to Create New Route Plan"),
      content: getTranslation(
        setting,
        "Orders",
        "You cannot create new route plan with orders on different dates. Please select orders on the same date and try again.",
      ),
    },
    A_ADD_ORDER_TO_EXIST_ROUTE_FAILED: {
      title: getTranslation(setting, "Orders", "Failed to Add to Existing Route Plan"),
      content: getTranslation(
        setting,
        "Orders",
        "You cannot add orders on different dates to an existing route plan. Please select orders on the same date and try again.",
      ),
    },
  };

  const routePlanType = query?.routePlanType || setting?.routePlanType || "PDP";
  const defaultParams = { routePlanType };

  useEffect(() => {
    doGetAllOrders({ currentPage, itemsPerPage, ...defaultParams, ...query });
  }, [query?.routePlanType]);

  useEffect(() => {
    let isMounted = true;
    socket.connect();
    socket.on(user.company?._id, data => {
      if (data.event === "orders.geocoding.completed" && isMounted) {
        doGetAllOrders({
          currentPage,
          itemsPerPage,
          ...defaultParams,
          ...query,
        });
      }
    });
    return () => {
      isMounted = false;
      socket.disconnect();
    };
  }, []);

  const handleFilter = (params, updatePath) => {
    if (updatePath) {
      pushTo({ query: params });
    }
    const currentPage = Number(params?.currentPage) || 1;
    const itemsPerPage = Number(params?.itemsPerPage) || PAGE_SIZE;
    doGetAllOrders({
      currentPage,
      itemsPerPage,
      ...defaultParams,
      ...params,
    });
  };

  const handleTableChange = params => {
    setDefaultSelectedItems([]);
    const newParams = { ...query, ...params };
    handleFilter(newParams, true);
  };

  const withAction = item => {
    const labels = allLabels.filter(l => item.labels.includes(l._id));
    return (
      <div className="actions" style={{ justifyContent: "right" }}>
        <div className="LabelComponent" style={{ flexFlow: "nowrap", width: "72px", justifyContent: "left" }}>
          {labels.map(l => (
            <Popover
              key={l._id}
              content={
                <div
                  className="TooltipTitle"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}>
                  <span
                    style={{
                      display: "flex",
                      height: "10px",
                      width: "10px",
                      backgroundColor: `#${l.color}`,
                      borderRadius: "2px",
                      marginRight: "4px",
                    }}
                  />{" "}
                  {l.name}
                </div>
              }
              className="LabelItem">
              <span
                style={{
                  height: "10px",
                  width: "10px",
                  backgroundColor: `#${l.color}`,
                  borderRadius: "2px",
                  marginTop: 0,
                }}
              />
            </Popover>
          ))}
        </div>
        <Link to={`/orders/${item._id}`}>
          <Button type="link" className="view">
            <ReactSVG className="icon" src="/icons/visibility.svg" />
          </Button>
        </Link>
        {canEdit && (
          <Popconfirm
            title={getTranslation(setting, "Common", "Are you sure to delete this item?")}
            onConfirm={() => handleClickDelete(item)}
            okText={getTranslation(setting, "Common", "Yes")}
            cancelText={getTranslation(setting, "Common", "No")}>
            <Button type="link" className="delete">
              <ReactSVG className="icon" src="/icons/delete.svg" />
            </Button>
          </Popconfirm>
        )}
        {!canEdit && item.routePlan && (
          <Link disabled to={`/route-plans/${item.routePlan}/orders`}>
            <Button type="link" className="view">
              <ReactSVG className="icon" src="/icons/map.svg" />
            </Button>
          </Link>
        )}
      </div>
    );
  };

  const handleClickDelete = async item => {
    doDeleteOrder(item._id);
  };

  const handleBulkActions = (selectedOrders, type) => {
    setState({ ...state, visibleModal: true, type, items: selectedOrders });
    if (type === "A_ADD_ORDER_TO_NEW_ROUTE") {
      form.setFieldsValue({ type: setting?.routePlanType });
    }
  };

  const handleSelectAll = async () => {
    // setDefaultSelectedItems(metadata.allOrders);
    // setState({ ...state, items: metadata.allOrders });
    const value = !isSelectAll;
    setIsSelectAll(value);
    if (value) {
      await doGetAllOrders({
        currentPage,
        itemsPerPage,
        ...query,
        isSelectAll: value,
      });
    } else {
      setDefaultSelectedItems([]);
      setState({ ...state, items: [] });
    }
  };

  useEffect(() => {
    let isMounted = true;
    if (metadata?.allOrders?.length > 0 && isMounted) {
      setDefaultSelectedItems(metadata.allOrders);
      setState({ ...state, items: metadata.allOrders });
    }
    return () => {
      isMounted = false;
    };
  }, [metadata]);

  const handleBulkUpdate = selectedOrders => {
    return {
      title: [
        <Button
          key={"select-all"}
          type={isSelectAll ? "primary" : "default"}
          className="btn-select-all"
          onClick={handleSelectAll}>
          {getTranslation(setting, "Common", "Select all")}
        </Button>,
        `${selectedOrders.length} ${getTranslation(setting, "Orders", "orders selected")}`,
      ],
      actions: (
        <Space>
          {ACTIONS.map(action => (
            <Button
              className={`btn-bulk-${action[2]}`}
              key={`bulk-${action[2]}`}
              onClick={() => handleBulkActions(selectedOrders, action[0])}
              type="primary"
              disabled={
                action[0] === "A_BULK_UPDATE_DELIVERY_DATE"
                  ? selectedOrders.filter(o => o.status === "DRAFT").length <= 0
                  : selectedOrders.length <= 0
              }>
              {action[1]}
              <ReactSVG className="icon" src={`/icons/bulk-${action[2]}.svg`} />
            </Button>
          ))}
        </Space>
      ),
    };
  };

  const handleSubmit = async values => {
    setModifying(true);
    if (state.type === "ORDER_TYPE") {
      dispatch(doUpdateSettingNoFetching({ routePlanType: values.type }));
      if (typeNewOrder === "upload") {
        history.push(`/orders/upload?routePlanType=${values.type}`);
      }
      if (typeNewOrder === "new") {
        history.push(`/orders/new?routePlanType=${values.type}`);
      }
    } else if (state.type === "A_DELETE") {
      await doBulkDeleteOrders({ orderIds: state.items.map(s => s._id) });
      setModifying(false);
    } else if (state.type === "A_UPDATE") {
      await doBulkUpdateOrders({
        ...values,
        orderIds: state.items.map(s => s._id),
      });
      setModifying(false);
    } else if (state.type === "A_BULK_UPDATE_DELIVERY_DATE") {
      await doBulkUpdateDeliveryDate({
        ...values,
        orderIds: state.items.map(s => s._id),
      });
      setModifying(false);
    } else {
      const dragOrders = state.items.filter(o => o.status === "DRAFT");
      if (dragOrders.length !== state.items.length) {
        setState({
          ...state,
          type: "A_FAILED",
          typeFailed:
            state.type === "A_ADD_ORDER_TO_NEW_ROUTE"
              ? "A_ADD_ORDER_TO_NEW_ROUTE_FAILED"
              : "A_ADD_ORDER_TO_EXIST_ROUTE_FAILED",
          typeFailedContent: getTranslation(
            setting,
            "Orders",
            "Only orders in draft status can be added to a new or existing route plan",
          ),
        });
      } else {
        if (state.type === "A_ADD_ORDER_TO_NEW_ROUTE") {
          const matchedOrders = state.items.filter(
            o => moment(o.deliveryDate).utc().format(DATE_FORMAT_QUERY) === values.deliveryDate,
          );
          if (matchedOrders.length !== state.items.length) {
            setState({
              ...state,
              type: "A_FAILED",
              typeFailed: "A_ADD_ORDER_TO_NEW_ROUTE_FAILED",
              typeFailedContent: null,
            });
          } else {
            dispatch(doCreateRoutePlan(values));
          }
        }
        if (state.type === "A_ADD_ORDER_TO_EXIST_ROUTE") {
          const routePlan = routePlans.find(r => r._id === values.routePlanId);
          const deliveryDate = moment(routePlan.deliveryDate).utc().format(DATE_FORMAT_QUERY);
          const matchedOrders = state.items.filter(
            o => moment(o.deliveryDate).utc().format(DATE_FORMAT_QUERY) === deliveryDate,
          );
          if (matchedOrders.length !== state.items.length) {
            setState({
              ...state,
              type: "A_FAILED",
              typeFailed: "A_ADD_ORDER_TO_EXIST_ROUTE_FAILED",
              typeFailedContent: null,
            });
          } else {
            dispatch(
              doAddOrdersRoutePlan({
                id: values.routePlanId,
                orders: state.items.map(s => s._id),
              }),
            );
            setModifying(false);
          }
        }
      }
    }
  };

  const handleCancel = () => {
    setState({ ...state, visibleModal: false });
    form.resetFields();
  };

  useEffect(() => {
    let isMounted = true;
    if (!isFetching && ["bulk_delete_succeeded", "bulk_update_succeeded"].includes(message) && isMounted) {
      handleCancel();
      const timeoutId = setTimeout(() => {
        if (message === "bulk_update_succeeded" && isMounted) {
          doGetAllOrders({ currentPage, itemsPerPage, ...query });
        }
      }, 2000);
      return () => {
        isMounted = false;
        clearTimeout(timeoutId);
      };
    }
    return () => {
      isMounted = false;
    };
  }, [isFetching, message]);

  useEffect(() => {
    let isMounted = true;
    if (!isFetchingRoutePlan && "add_orders_to_routeplan_succeeded" === messageRoutePlan && isMounted) {
      handleCancel();
      const timeoutId = setTimeout(() => {
        if (messageRoutePlan === "add_orders_to_routeplan_succeeded" && isMounted) {
          doGetAllOrders({ currentPage, itemsPerPage, ...query });
        }
      }, 2000);
      return () => {
        isMounted = false;
        clearTimeout(timeoutId);
      };
    }
    return () => {
      isMounted = false;
    };
  }, [isFetchingRoutePlan, messageRoutePlan]);

  useEffect(() => {
    let isMounted = true;
    if (!isFetchingRoutePlan && messageRoutePlan?.includes("create_routeplan_succeeded") && isMounted) {
      dispatch(
        doAddOrdersRoutePlan({
          id: messageRoutePlan?.split("_")[3],
          orders: state.items.map(s => s._id),
        }),
      );
      setModifying(false);
    }
    return () => {
      isMounted = false;
    };
  }, [isFetchingRoutePlan, messageRoutePlan]);

  const handleClickNewOrders = type => {
    if (!mountedRef.current) return;

    if (type === "upload") {
      history.push("/orders/upload");
    } else {
      history.push("/orders/new");
    }
  };

  return (
    <div className="Content OrdersPage">
      <RoutePlanCurrent />
      <div className="ContentHeader">
        <div className="HeaderTitle">{getTranslation(setting, "Orders", "Orders")}</div>
        <div className="HeaderToolBar">
          <div className="ToolBarButtons">
            <Button
              className={`FilterButton ${showFilters ? "Open" : "Close"}`}
              type="primary"
              onClick={() => setShowFilters(!showFilters)}>
              {getTranslation(setting, "Common", "Filter")}
              <ReactSVG className="icon" src={`/icons/filter${showFilters ? "-close" : ""}.svg`} />
            </Button>
            {canEdit && (
              <Dropdown
                menu={{
                  items: [
                    {
                      key: "upload",
                      label: (
                        <Button type="link" onClick={() => handleClickNewOrders("upload")}>
                          {getTranslation(setting, "Orders", "Upload")}
                          <ReactSVG className="icon" src="/icons/upload.svg" />
                        </Button>
                      ),
                    },
                    {
                      key: "new",
                      label: (
                        <Button type="link" onClick={() => handleClickNewOrders("new")}>
                          {getTranslation(setting, "Orders", "Single Order")}
                          <ReactSVG className="icon" src="/icons/manual.svg" />
                        </Button>
                      ),
                    },
                  ],
                }}
                placement="bottomRight"
                destroyPopupOnHide={true}>
                <Button size="large" className="NewButton" type="primary">
                  {getTranslation(setting, "Orders", "New Order")}
                  <ReactSVG className="icon" src="/icons/add.svg" />
                </Button>
              </Dropdown>
            )}
          </div>
        </div>
      </div>
      <div className="ContentFilters">
        {showFilters && (
          <OrdersFilterForm
            isFetching={isFetching}
            query={query}
            onSubmit={handleFilter}
            routePlanType={routePlanType}
          />
        )}
      </div>
      <div className="ContentMain">
        <OrdersTable
          rowKey="_id"
          loading={isFetching}
          roles={roles}
          query={query}
          withAction={withAction}
          pagination={pagination}
          dataSource={orders}
          onChange2={handleTableChange}
          scrollHeight="calc(100vh - 350px)"
          actionWidth={190}
          bulkUpdate={canEdit ? handleBulkUpdate : null}
          modifying={modifying}
          defaultSelectedItems={defaultSelectedItems}
          setDefaultSelectedItems={setDefaultSelectedItems}
          setting={setting}
          routePlanType={routePlanType}
        />
      </div>
      <Modal
        open={state.visibleModal}
        onCancel={handleCancel}
        forceRender
        width={state.type === "A_UPDATE" ? 1024 : state.type === "A_UPDATE" ? 600 : 370}>
        <>
          {state.type === "A_DELETE" && (
            <BulkDeleteForm
              form={form}
              isFetching={isFetching}
              items={state.items}
              handleSubmit={handleSubmit}
              handleCancel={handleCancel}
            />
          )}
          {state.type === "A_UPDATE" && (
            <BulkUpdateForm
              form={form}
              isFetching={isFetching}
              handleSubmit={handleSubmit}
              handleCancel={handleCancel}
              countryCodeInit={countryCodeInit}
            />
          )}
          {state.type === "A_BULK_UPDATE_DELIVERY_DATE" && (
            <BulkUpdateDeliveryDateForm
              form={form}
              isFetching={isFetching}
              handleSubmit={handleSubmit}
              handleCancel={handleCancel}
            />
          )}
          {state.type === "A_ADD_ORDER_TO_NEW_ROUTE" && (
            <RoutePlanForm
              form={form}
              isFetching={isFetching}
              routePlan={null}
              handleSubmit={handleSubmit}
              handleCancel={handleCancel}
              createWithOrders={true}
            />
          )}
          {state.type === "A_ADD_ORDER_TO_EXIST_ROUTE" && (
            <BulkAddToRoutePlanForm
              form={form}
              isFetching={isFetching}
              handleSubmit={handleSubmit}
              handleCancel={handleCancel}
            />
          )}
          {state.type === "ORDER_TYPE" && (
            <OrderTypeForm form={form} handleSubmit={handleSubmit} handleCancel={handleCancel} />
          )}
          {state.type === "A_FAILED" && (
            <div className="BulkFailed">
              <div className="FormHeader">
                <div className="left">{BULK_FAILED[state.typeFailed].title}</div>
              </div>
              <div className="FormContent">
                {state.typeFailedContent ? (
                  <p>{state.typeFailedContent}</p>
                ) : (
                  <p>{BULK_FAILED[state.typeFailed].content}</p>
                )}
              </div>
              <div className="FormFooter">
                <Button className="btn-i-got-this" onClick={handleCancel} block>
                  {getTranslation(setting, "Orders", "OK I got this")}
                  <ReactSVG className="icon" src="/icons/check.svg" />
                </Button>
              </div>
            </div>
          )}
        </>
      </Modal>
    </div>
  );
};

export default withPage(ListContainer(OrdersPage));
